import { NotificationPayload, NotificationTopicSchema } from '@sit/notifications-shared';
import {
  invalidateAllTimesheetsQueries,
  invalidateCrewTimesheetsQuery,
  invalidateEverythingTimesheetsRelated,
  useInvalidateGetApprovalEntries,
  useInvalidateGetApprovalTimesheets,
  useInvalidateGetTimesheetRows,
  useInvalidateTimesheet,
} from '@sit/openapi';
import { useQueryClient } from '@tanstack/react-query';
import { useTimesheetSaveSettled } from '@web/api/timesheet/use-post-timesheet-save-settled';
import { useTimesheetSubmitSettled } from '@web/api/timesheet/use-post-timesheet-submit-settled';
import { PropsWithChildren, useCallback } from 'react';
import { useOnLatestNotification } from './hooks/useLatestNotification';

function useHandleGlobalNotifications() {
  const queryClient = useQueryClient();
  const onSaveSettled = useTimesheetSaveSettled();
  const onSubmitSettled = useTimesheetSubmitSettled();
  const invalidateTimesheetsQuery = useInvalidateGetApprovalTimesheets();
  const invalidateEntriesQuery = useInvalidateGetApprovalEntries();
  const invalidateTimesheetRowsQuery = useInvalidateGetTimesheetRows();
  const invalidateTimesheetQuery = useInvalidateTimesheet();
  const handleApprovals = useCallback(
    (notification: NotificationPayload) => {
      void invalidateTimesheetsQuery();
      void invalidateCrewTimesheetsQuery(queryClient);
      // Some approval notifications omit affected timesheet IDs. Invalidate cached
      // details too, so reopening a timesheet cannot reuse its pre-approval status.
      void invalidateEverythingTimesheetsRelated(queryClient);
      void invalidateEntriesQuery();
      const invalidateTs = (timesheetId: string) => {
        void invalidateTimesheetQuery(timesheetId);
        void invalidateTimesheetRowsQuery(timesheetId);
      };

      const { data } = notification;
      if (!data) return;

      if ('timesheets' in data && Array.isArray(data.timesheets)) {
        for (const timesheetId of data.timesheets) {
          if (typeof timesheetId !== 'string') continue;
          invalidateTs(timesheetId);
        }
      } else if ('timesheetMapping' in data) {
        const { timesheetMapping } = data as {
          timesheetMapping: Record<string, { timesheetId: string }>;
        };
        const timesheetIds = [...new Set(Object.values(timesheetMapping).map((mapping) => mapping.timesheetId))];
        for (const timesheetId of timesheetIds) {
          invalidateTs(timesheetId);
        }
      }
    },
    [invalidateEntriesQuery, invalidateTimesheetQuery, invalidateTimesheetRowsQuery, invalidateTimesheetsQuery, queryClient],
  );

  useOnLatestNotification(NotificationTopicSchema.enum.APPROVALS, handleApprovals);
  const handleSave = useCallback(
    async (notification: NotificationPayload) => {
      const { data } = notification;
      await onSaveSettled(data?.timesheetId, notification.type === 'SUCCESS');
    },
    [onSaveSettled],
  );
  useOnLatestNotification(NotificationTopicSchema.enum.SAVE_TIMESHEET, handleSave);
  const handleSubmit = useCallback(
    async (notification: NotificationPayload) => {
      const { data } = notification;
      await onSubmitSettled(data?.timesheetId, notification.type === 'SUCCESS');
      if (data && 'timesheetId' in data) {
        void invalidateTimesheetQuery(data.timesheetId);
        void invalidateAllTimesheetsQueries(queryClient);
        if (notification.type === 'SUCCESS') {
          void invalidateEverythingTimesheetsRelated(queryClient);
        }
      }
    },
    [onSubmitSettled, invalidateTimesheetQuery, queryClient],
  );
  useOnLatestNotification(NotificationTopicSchema.enum.SUBMIT_TIMESHEET, handleSubmit);
}

export function NotificationGlobalHandler({ children }: PropsWithChildren) {
  useHandleGlobalNotifications();
  return <>{children}</>;
}
